import { post, isApiConfigured } from './api';
import { company } from '../data/company';

/**
 * The three form submissions on the site. Request/response shapes are frozen in
 * docs/API-CONTRACT.md so the Flask routes can be written against them.
 */

// Deliberately permissive: rejecting unusual-but-valid addresses loses leads.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateEmail(value) {
  const email = value.trim();
  if (!email) return 'Please enter your email address.';
  if (!EMAIL.test(email)) return 'That does not look like a valid email address.';
  return null;
}

/**
 * Builds a prefilled mailto: URL.
 *
 * Used as the delivery path while REACT_APP_API_BASE_URL is unset. It is not a
 * substitute for server-side capture — it hands the message to the visitor's
 * own mail client, so nothing is recorded and the visitor can still abandon it —
 * but it does mean the button actually delivers something today rather than
 * failing. As soon as the Flask routes in docs/API-CONTRACT.md exist, setting
 * the env var switches every form to a real POST with no code change.
 */
export function buildMailto(subject, lines) {
  // Drop omitted fields (null) but keep intentional '' separators — filtering
  // on truthiness removes both, and the body loses the blank line that
  // separates the message from the contact details.
  const body = lines.filter((line) => line != null).join('\n');
  return `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Resolves to 'api' when the backend accepted it, 'mailto' when we handed the
 * message to the visitor's mail client instead.
 *
 * The API is tried first and mailto is the fallback, rather than an either/or.
 * The endpoints exist in the code on the dev branch but only go live when that
 * server is redeployed and migrated, so between now and then a POST would 404;
 * falling through means the visitor still reaches someone either way, and the
 * switch to real capture happens on deploy with no change here.
 *
 * Only a *structured* error is surfaced — one where the server returned our
 * documented envelope, meaning it understood the request and is telling the
 * visitor something they can fix (a rejected address, a rate limit). A bare
 * 404 or a gateway error means the endpoint is not deployed, which is not the
 * visitor's problem, so that falls through to mailto. Showing "that did not go
 * through" for a 404 would strand them; opening a mail client in response to
 * "that email looks wrong" would hide the thing they need to correct.
 */
function deliver(path, payload, subject, lines) {
  const handOff = () => {
    // assign() rather than `location.href = ...` so it can be stubbed in tests.
    window.location.assign(buildMailto(subject, lines));
    return 'mailto';
  };

  if (!isApiConfigured()) return Promise.resolve(handOff());

  return post(path, payload)
    .then(() => 'api')
    .catch((err) => {
      if (err?.structured) throw err;
      if (process.env.NODE_ENV === 'development') {
        console.warn('[forms] API unavailable, falling back to mailto:', err?.message);
      }
      return handOff();
    });
}

export const submitDemoRequest = (email) =>
  deliver(
    '/v1/leads/demo',
    { email: email.trim(), source: 'landing-demo' },
    'Demo request',
    [`Please get in touch about a NimblePark demo.`, '', `Email: ${email.trim()}`],
  );

export const submitNewsletter = (email) =>
  deliver(
    '/v1/leads/newsletter',
    { email: email.trim(), source: 'footer' },
    'Newsletter signup',
    [`Please add me to the NimblePark mailing list.`, '', `Email: ${email.trim()}`],
  );

export function validateContact({ name, email, message }) {
  const errors = {};
  if (!name?.trim()) errors.name = 'Please tell us your name.';
  const emailError = validateEmail(email ?? '');
  if (emailError) errors.email = emailError;
  if (!message?.trim()) errors.message = 'Please add a short message.';
  return errors;
}

export const submitContact = ({ name, email, phone, message }) =>
  deliver(
    '/v1/contact',
    {
      name: name.trim(),
      email: email.trim(),
      phone: phone?.trim() || undefined,
      message: message.trim(),
    },
    `Enquiry from ${name.trim()}`,
    [
      message.trim(),
      '',
      '---',
      `Name: ${name.trim()}`,
      `Email: ${email.trim()}`,
      phone?.trim() ? `Phone: ${phone.trim()}` : null,
    ],
  );
